"use client";
import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Tabs, Tab, TabList, TabPanel } from "../../../molecules/tabion";
import MenuItems from "./menuItems";
import { SHOP_BY_CATEGORY, SHOP_BY_OCCASION } from "../../../../constants/common/headerToolbarConstants";
import useAnalytics from "../../../../analytics/useAnalytics";
import PlpSkeleton from "../../../molecules/skeletons/plpSkeleton";
import useNavigationReset from "../../../../customHooks/useNavigationReset";
import { useDispatch } from "react-redux";
import { setAllGiftsActiveTab } from "../../../../store/slices/commonSlice";
import { cleverTapCommonEvents } from "../../../../analytics/clevertapConstants";

/**
 * MobileMegaMenu component for displaying a mobile-friendly mega menu.
 *
 * @param {object} props - Component props.
 * @param {Array} props.data - Array of menu item data objects.
 * @returns {JSX.Element} JSX for the MobileMegaMenu component.
 */
const MobileMegaMenu = ({ data = [] }) => {
  const { init, trackEvent } = useAnalytics();
  const dispatch = useDispatch();
  const [selectedTab, setSelectedTab] = useState(0);
  const isNavigating = useNavigationReset();
  const modifiedCategories = data?.reduce((accumulator, currentObject) => {
    if (
      currentObject?.categoryName?.includes(SHOP_BY_CATEGORY) ||
      currentObject?.categoryName?.includes(SHOP_BY_OCCASION)
    ) {
      return accumulator.concat(currentObject);
    }
    return accumulator;
  }, []);

  useEffect(() => {
    init();
  }, []);

  useEffect(() => {
    if (selectedTab >= 0) {
      dispatch(setAllGiftsActiveTab(modifiedCategories?.[selectedTab]?.categoryName));
    }
  }, [selectedTab]);

  const onTabChange = useCallback((evt) => {
    setSelectedTab(evt);
    trackEvent(
      cleverTapCommonEvents?.allGiftsClicked,
      {
        page_name: "All Gifts",
        page_type: "All Gifts",
        tab: modifiedCategories?.[evt]?.categoryName,
      },
      ["clevertap"],
    );
  }, []);

  if (isNavigating) {
    return (
      <div className="fixed inset-0 z-[9999] mt-[46px] overflow-hidden bg-white-900">
        <PlpSkeleton isMobile={true} />
      </div>
    );
  }

  return (
    <div className="w-full p-16 pb-88">
      <Tabs>
        <TabList
          className={`-mt-3 mb-20 w-[150px] max-w-full items-center overflow-hidden text-ellipsis whitespace-nowrap !border-t-0 text-center text-14 font-400 leading-24`}
          isActiveStyle="border-b-2 border-darkOliveGreen text-fnp-400 font-600 pb-4 mt-0"
          tabListStyle="justify-center gap-16"
          onTabSelected={onTabChange}
          selected={selectedTab}
        >
          {modifiedCategories.map((title, index) => {
            return (
              <Tab key={title.productCategoryId} id={index}>
                {title.categoryName}
              </Tab>
            );
          })}
        </TabList>
        {modifiedCategories.map((items, index) => (
          <TabPanel key={items.productCategoryId} id={index} className="overflow-auto">
            <div className="flex flex-col gap-12">
              {items?.childCategoryLists?.map((subItem, i) => (
                <MenuItems key={`Level-0-${i}`} data={subItem} itemIndex={i} itemLevel={0} />
              ))}
            </div>
          </TabPanel>
        ))}
      </Tabs>
    </div>
  );
};

MobileMegaMenu.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      categoryName: PropTypes.string,
      productCategoryId: PropTypes.string,
      promo: PropTypes.string,
      childCategoryLists: PropTypes.array,
    }),
  ).isRequired,
};

export default React.memo(MobileMegaMenu);
